import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Repeat, Upload, X, FileText, Loader2, Eye, Download, Trash2 } from 'lucide-react';
import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';
import { ContaPagar, ContratoAberto, TipoContratoFinanceiro } from '@/services/financeiroService';
import { toast } from 'sonner';
import { parseDateLocal } from './contasPagarUtils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CATEGORIA_COMISSAO_CARREGADORES_PARCEIRO, CATEGORIA_COMISSAO_PARCEIRO_PAI } from './contasPagarUtils';
import { supabase } from '@/integrations/supabase/client';
import { configuracoesFinanceiroService, ConfiguracoesFinanceiro } from '@/services/configuracoesFinanceiroService';
import { getBoletosDaConta, getNotasFiscaisDaConta } from '@/utils/contasAnexos';

// Tipos para o formData
export interface FormDataContaPagar {
  descricao: string;
  valor: string;
  data_vencimento: string;
  categoria: string;
  fornecedor: string;
  contrato_id: string;
  contrato_tipo: TipoContratoFinanceiro | '';
  parceiro_id: string;
  observacoes: string;
  tipo_pagamento: 'avista' | 'parcelado' | 'recorrente' | '';
  forma_pagamento: string;
  quantidade_parcelas: number | undefined;
  modalidade_parcelamento: 'mesmo_dia' | '28_dias' | '';
  isRecorrente: boolean;
  frequencia: 'diaria' | 'semanal' | 'quinzenal' | 'mensal' | 'trimestral' | 'semestral' | 'anual';
  dia_semana: number | undefined;
  dia_mes: number | undefined;
  intervalo_personalizado: number | undefined;
  data_fim_recorrencia: string;
}

interface ContasPagarDialogsProps {
  // Dialog principal de edição/criação
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  contaSelecionada: ContaPagar | null;
  formData: FormDataContaPagar;
  setFormData: (data: FormDataContaPagar | ((prev: FormDataContaPagar) => FormDataContaPagar)) => void;
  salvarConta: () => Promise<void>;
  deletarComprovante: (contaId: string) => Promise<void>;
  deletarBoleto: (contaId: string, url?: string) => Promise<void>;
  deletarNotaFiscal: (contaId: string, url?: string) => Promise<void>;
  comprovanteFileForm: File | null;
  setComprovanteFileForm: (file: File | null) => void;
  boletoFilesForm: File[];
  setBoletoFilesForm: Dispatch<SetStateAction<File[]>>;
  notaFiscalFilesForm: File[];
  setNotaFiscalFilesForm: Dispatch<SetStateAction<File[]>>;
  
  // Dialog de exclusão
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: (open: boolean) => void;
  deletarOpcao: 'apenas' | 'todas' | 'futuras';
  setDeletarOpcao: (opcao: 'apenas' | 'todas' | 'futuras') => void;
  deletarConta: () => Promise<void>;
  
  // Dialog de escopo de edição
  editarEscopoDialogOpen: boolean;
  setEditarEscopoDialogOpen: (open: boolean) => void;
  editarEscopo: 'apenas' | 'futuras' | 'todas';
  setEditarEscopo: (escopo: 'apenas' | 'futuras' | 'todas') => void;
  dadosParaSalvar: Partial<ContaPagar> | null;
  setDadosParaSalvar: (dados: Partial<ContaPagar> | null) => void;
  aplicarAlteracoesParcela: () => Promise<void>;
  
  // Dialog de alerta de parcelas
  alertaParcelasDialogOpen: boolean;
  setAlertaParcelasDialogOpen: (open: boolean) => void;
  alertaParcelasInfo: { tipo: 'reducao' | 'aumento'; quantidadeAtual: number; quantidadeNova: number } | null;
  setAlertaParcelasInfo: (info: { tipo: 'reducao' | 'aumento'; quantidadeAtual: number; quantidadeNova: number } | null) => void;
  carregarContas: () => Promise<void>;
  
  // Dialog de upload de comprovante
  comprovanteDialogOpen: boolean;
  setComprovanteDialogOpen: (open: boolean) => void;
  comprovanteFile: File | null;
  setComprovanteFile: (file: File | null) => void;
  uploadingComprovante: boolean;
  fazerUploadComprovante: () => Promise<void>;
  
  // Dialog de documentos
  documentosDialogOpen: boolean;
  setDocumentosDialogOpen: (open: boolean) => void;
  contaParaDocumentos: ContaPagar | null;
  visualizarArquivo: (url: string) => void;
  baixarArquivo: (url: string, nomeArquivo: string) => Promise<void>;
  
  // Contratos em aberto
  contratosAberto: ContratoAberto[];
  carregandoContratos: boolean;
}

export function ContasPagarDialogs({
  dialogOpen,
  setDialogOpen,
  contaSelecionada,
  formData,
  setFormData,
  salvarConta,
  deletarComprovante,
  deletarBoleto,
  deletarNotaFiscal,
  comprovanteFileForm,
  setComprovanteFileForm,
  boletoFilesForm,
  setBoletoFilesForm,
  notaFiscalFilesForm,
  setNotaFiscalFilesForm,
  deleteDialogOpen,
  setDeleteDialogOpen,
  deletarOpcao,
  setDeletarOpcao,
  deletarConta,
  editarEscopoDialogOpen,
  setEditarEscopoDialogOpen,
  editarEscopo,
  setEditarEscopo,
  dadosParaSalvar,
  setDadosParaSalvar,
  aplicarAlteracoesParcela,
  alertaParcelasDialogOpen,
  setAlertaParcelasDialogOpen,
  alertaParcelasInfo,
  setAlertaParcelasInfo,
  carregarContas,
  comprovanteDialogOpen,
  setComprovanteDialogOpen,
  comprovanteFile,
  setComprovanteFile,
  uploadingComprovante,
  fazerUploadComprovante,
  documentosDialogOpen,
  setDocumentosDialogOpen,
  contaParaDocumentos,
  visualizarArquivo,
  baixarArquivo,
  contratosAberto,
  carregandoContratos,
}: ContasPagarDialogsProps) {
  const [listaParceiros, setListaParceiros] = useState<{ id: string; nome: string | null }[]>([]);
  const [config, setConfig] = useState<ConfiguracoesFinanceiro | null>(null);

  useEffect(() => {
    configuracoesFinanceiroService.getConfig().then(setConfig);
  }, []);

  useEffect(() => {
    if (!dialogOpen || (formData.categoria !== CATEGORIA_COMISSAO_CARREGADORES_PARCEIRO && formData.categoria !== CATEGORIA_COMISSAO_PARCEIRO_PAI)) return;
    (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, nome')
        .eq('role', 'parceiro')
        .order('nome');
      if (!error) setListaParceiros(data ?? []);
    })();
  }, [dialogOpen, formData.categoria]);

  // Identificação automática: quando categoria é "Comissões Parceiros (Pai)" e o contrato é de carregador
  // cujo lead pertence a um parceiro filho, pré-preenche o parceiro pai (metodologia igual às outras comissões).
  useEffect(() => {
    if (
      !dialogOpen ||
      formData.categoria !== CATEGORIA_COMISSAO_PARCEIRO_PAI ||
      !formData.contrato_id ||
      !formData.contrato_id.trim()
    ) return;
    const contrato = contratosAberto.find((c) => c.id === formData.contrato_id);
    if (contrato?.tipo_contrato !== 'carregador') return;
    (async () => {
      const { data: cc } = await (supabase.from('contratos_carregadores' as any) as any)
        .select('lead_id')
        .eq('id', formData.contrato_id)
        .maybeSingle();
      if (!cc?.lead_id) return;
      const { data: lead } = await supabase
        .from('leads')
        .select('parceiro_id')
        .eq('id', cc.lead_id)
        .maybeSingle();
      if (!lead?.parceiro_id) return;
      const { data: pr } = await supabase
        .from('profiles')
        .select('parceiro_cadastrado_por')
        .eq('id', lead.parceiro_id)
        .maybeSingle();
      const paiId = pr?.parceiro_cadastrado_por ?? '';
      if (paiId && !formData.parceiro_id) {
        setFormData((prev) => ({ ...prev, parceiro_id: paiId }));
      }
    })();
  }, [dialogOpen, formData.categoria, formData.contrato_id, formData.parceiro_id, contratosAberto]);

  const isCategoriaComissaoParceiro = formData.categoria === CATEGORIA_COMISSAO_CARREGADORES_PARCEIRO || formData.categoria === CATEGORIA_COMISSAO_PARCEIRO_PAI;

  return (
    <>
      {/* Dialog principal de edição/criação */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {contaSelecionada ? 'Editar Conta a Pagar' : 'Nova Conta a Pagar'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados da conta a pagar
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Contrato em primeiro lugar para informar se a despesa é relacionada a algum contrato */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contrato_id">Contrato</Label>
                <Select 
                  value={formData.contrato_id || undefined} 
                  onValueChange={(value) => {
                    const contrato = value === "__none__" ? null : contratosAberto.find((c) => c.id === value);
                    setFormData({
                      ...formData,
                      contrato_id: value === "__none__" ? '' : value,
                      contrato_tipo: contrato?.tipo_contrato ?? (value === "__none__" ? '' : 'fotovoltaico'),
                    });
                  }}
                  disabled={carregandoContratos}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={carregandoContratos ? "Carregando..." : "Selecione um contrato"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum</SelectItem>
                    {contratosAberto.map((contrato) => (
                      <SelectItem key={contrato.id} value={contrato.id}>
                        {contrato.cliente_nome} ({contrato.tipo_contrato === 'carregador' ? 'Carregador' : 'Fotovoltaico'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Vincule esta despesa a um contrato para rastrear os custos do projeto
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição *</Label>
                <Input
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Ex: Pagamento fornecedor"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valor">Valor</Label>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  value={formData.valor}
                  onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                  placeholder="Deixe em branco se ainda não souber o valor"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="data_vencimento">Data de Vencimento *</Label>
                <Input
                  id="data_vencimento"
                  type="date"
                  value={formData.data_vencimento}
                  onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="categoria">Categoria *</Label>
                <Select value={formData.categoria} onValueChange={(value) => setFormData({ ...formData, categoria: value, parceiro_id: (value === CATEGORIA_COMISSAO_CARREGADORES_PARCEIRO || value === CATEGORIA_COMISSAO_PARCEIRO_PAI) ? formData.parceiro_id : '' })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.contasPagar.categorias || []).map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isCategoriaComissaoParceiro && (
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="parceiro_id">Parceiro *</Label>
                  <Select
                    value={formData.parceiro_id || undefined}
                    onValueChange={(value) => setFormData({ ...formData, parceiro_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={formData.categoria === CATEGORIA_COMISSAO_PARCEIRO_PAI ? "Selecione o parceiro pai" : "Selecione o parceiro"} />
                    </SelectTrigger>
                    <SelectContent>
                      {listaParceiros.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.nome ?? p.id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {formData.categoria === CATEGORIA_COMISSAO_PARCEIRO_PAI
                      ? "Para comissão do parceiro pai: selecione o parceiro que recebe (pai da rede). O contrato deve ser de carregador fechado por um parceiro filho."
                      : "O pagamento aparecerá na aba Financeiro do parceiro quando marcado como pago em Contas a Pagar."}
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tipo_pagamento">Tipo de Pagamento</Label>
                <Select 
                  value={formData.tipo_pagamento || undefined} 
                  onValueChange={(value) => {
                    const tipoPagamento = value as 'avista' | 'parcelado' | 'recorrente';
                    setFormData({ 
                      ...formData, 
                      tipo_pagamento: tipoPagamento,
                      quantidade_parcelas: tipoPagamento === 'parcelado' ? formData.quantidade_parcelas : undefined,
                      modalidade_parcelamento: tipoPagamento === 'parcelado' ? (formData.modalidade_parcelamento || 'mesmo_dia') : '',
                      isRecorrente: tipoPagamento === 'recorrente',
                      frequencia: tipoPagamento === 'recorrente' ? formData.frequencia : 'mensal',
                      dia_semana: tipoPagamento === 'recorrente' ? formData.dia_semana : undefined,
                      dia_mes: tipoPagamento === 'recorrente' ? formData.dia_mes : undefined,
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const tiposCanonicos = ['avista', 'parcelado', 'recorrente'] as const;
                      const tiposDaConfig = config?.contasPagar?.tiposPagamento || tiposCanonicos;
                      const tiposParaSelect = [...new Set([...tiposCanonicos, ...tiposDaConfig])];
                      return tiposParaSelect.map(tipo => (
                        <SelectItem key={tipo} value={tipo}>
                          {tipo === 'avista' ? 'À Vista' : tipo === 'parcelado' ? 'Parcelado' : tipo === 'recorrente' ? 'Recorrente' : tipo}
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="forma_pagamento">Forma de Pagamento</Label>
                <Select value={formData.forma_pagamento} onValueChange={(value) => setFormData({ ...formData, forma_pagamento: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.contasPagar.formasPagamento || []).map(forma => (
                      <SelectItem key={forma} value={forma}>{forma}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formData.tipo_pagamento === 'parcelado' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="quantidade_parcelas">Quantidade de Parcelas *</Label>
                  <Input
                    id="quantidade_parcelas"
                    type="number"
                    min="1"
                    value={formData.quantidade_parcelas || ''}
                    onChange={(e) => setFormData({ ...formData, quantidade_parcelas: parseInt(e.target.value) || undefined })}
                    placeholder="Ex: 12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modalidade_parcelamento">Modalidade de Parcelamento</Label>
                  <Select
                    value={formData.modalidade_parcelamento || 'mesmo_dia'}
                    onValueChange={(value) => setFormData({ ...formData, modalidade_parcelamento: value as 'mesmo_dia' | '28_dias' })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mesmo_dia">Mesmo dia do mês (ex: dia 15 de cada mês)</SelectItem>
                      <SelectItem value="28_dias">A cada 28 dias (próxima parcela 28 dias após a anterior)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fornecedor">Beneficiário</Label>
                <Input
                  id="fornecedor"
                  value={formData.fornecedor}
                  onChange={(e) => setFormData({ ...formData, fornecedor: e.target.value })}
                  placeholder="Nome do beneficiário"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Informações adicionais..."
                rows={3}
              />
            </div>

            {/* Campo de Upload de Comprovante */}
            <div className="space-y-2">
              <Label htmlFor="comprovante-form">
                Comprovante de Pagamento
                {contaSelecionada?.comprovante_url && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    (Comprovante atual: {contaSelecionada.comprovante_nome_arquivo || 'Ver comprovante'})
                  </span>
                )}
              </Label>
              {contaSelecionada?.comprovante_url && (
                <div className="p-3 border rounded-md bg-muted flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="text-sm">
                      {contaSelecionada.comprovante_nome_arquivo || 'Comprovante anexado'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(contaSelecionada.comprovante_url, '_blank')}
                    >
                      Ver
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => deletarComprovante(contaSelecionada.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  id="comprovante-form"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('Arquivo muito grande. Tamanho máximo: 5MB');
                        return;
                      }
                      setComprovanteFileForm(file);
                    }
                  }}
                  className="hidden"
                />
                <Label
                  htmlFor="comprovante-form"
                  className="cursor-pointer flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent"
                >
                  <Upload className="h-4 w-4" />
                  {comprovanteFileForm ? comprovanteFileForm.name : 'Selecionar arquivo'}
                </Label>
                {comprovanteFileForm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setComprovanteFileForm(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: PDF, JPG, PNG (máx. 5MB)
              </p>
            </div>

            {/* Campo de Upload de Boleto */}
            <div className="space-y-2">
              <Label htmlFor="boleto-form">Boleto</Label>
              {contaSelecionada && getBoletosDaConta(contaSelecionada).length > 0 && (
                <div className="space-y-2">
                  {getBoletosDaConta(contaSelecionada).map((anexo) => (
                    <div
                      key={anexo.id}
                      className="p-3 border rounded-md bg-muted flex items-center justify-between gap-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 shrink-0" />
                        <span className="text-sm truncate">
                          {anexo.nome_arquivo || 'Boleto anexado'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(anexo.url, '_blank')}
                        >
                          Ver
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => deletarBoleto(contaSelecionada.id, anexo.url)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {boletoFilesForm.length > 0 && (
                <div className="space-y-2">
                  {boletoFilesForm.map((file) => {
                    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
                    return (
                    <div
                      key={fileKey}
                      className="p-3 border rounded-md border-dashed flex items-center justify-between gap-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Upload className="h-4 w-4 shrink-0" />
                        <span className="text-sm truncate">{file.name}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setBoletoFilesForm((prev) =>
                            prev.filter(
                              (f) =>
                                `${f.name}-${f.size}-${f.lastModified}` !== fileKey
                            )
                          )
                        }
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  id="boleto-form"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    const validos: File[] = [];
                    for (const file of files) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error(`Arquivo muito grande: ${file.name}. Tamanho máximo: 5MB`);
                        continue;
                      }
                      validos.push(file);
                    }
                    if (validos.length > 0) {
                      setBoletoFilesForm((prev) => [...prev, ...validos]);
                    }
                    e.target.value = '';
                  }}
                  className="hidden"
                />
                <Label
                  htmlFor="boleto-form"
                  className="cursor-pointer flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent"
                >
                  <Upload className="h-4 w-4" />
                  Selecionar arquivos
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: PDF, JPG, PNG (máx. 5MB cada). É possível anexar vários boletos.
              </p>
            </div>

            {/* Campo de Upload de Nota Fiscal */}
            <div className="space-y-2">
              <Label htmlFor="nota-fiscal-form">Nota Fiscal</Label>
              {contaSelecionada && getNotasFiscaisDaConta(contaSelecionada).length > 0 && (
                <div className="space-y-2">
                  {getNotasFiscaisDaConta(contaSelecionada).map((anexo) => (
                    <div
                      key={anexo.id}
                      className="p-3 border rounded-md bg-muted flex items-center justify-between gap-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 shrink-0" />
                        <span className="text-sm truncate">
                          {anexo.nome_arquivo || 'Nota fiscal anexada'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(anexo.url, '_blank')}
                        >
                          Ver
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => deletarNotaFiscal(contaSelecionada.id, anexo.url)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {notaFiscalFilesForm.length > 0 && (
                <div className="space-y-2">
                  {notaFiscalFilesForm.map((file) => {
                    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
                    return (
                    <div
                      key={fileKey}
                      className="p-3 border rounded-md border-dashed flex items-center justify-between gap-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Upload className="h-4 w-4 shrink-0" />
                        <span className="text-sm truncate">{file.name}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setNotaFiscalFilesForm((prev) =>
                            prev.filter(
                              (f) =>
                                `${f.name}-${f.size}-${f.lastModified}` !== fileKey
                            )
                          )
                        }
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  id="nota-fiscal-form"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    const validos: File[] = [];
                    for (const file of files) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error(`Arquivo muito grande: ${file.name}. Tamanho máximo: 5MB`);
                        continue;
                      }
                      validos.push(file);
                    }
                    if (validos.length > 0) {
                      setNotaFiscalFilesForm((prev) => [...prev, ...validos]);
                    }
                    e.target.value = '';
                  }}
                  className="hidden"
                />
                <Label
                  htmlFor="nota-fiscal-form"
                  className="cursor-pointer flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent"
                >
                  <Upload className="h-4 w-4" />
                  Selecionar arquivos
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: PDF, JPG, PNG (máx. 5MB cada). É possível anexar várias notas fiscais.
              </p>
            </div>
            
            {/* Seção de Recorrência */}
            {(formData.tipo_pagamento === 'recorrente' || formData.isRecorrente) && (
            <div className="border-t pt-4 space-y-4">
              {formData.tipo_pagamento === 'recorrente' ? (
                <div className="flex items-center gap-2">
                  <Repeat className="h-4 w-4" />
                  <Label className="text-base font-semibold">Configuração de Recorrência</Label>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isRecorrente"
                    checked={formData.isRecorrente}
                    onChange={(e) => setFormData({ ...formData, isRecorrente: e.target.checked, frequencia: 'mensal', dia_semana: undefined, dia_mes: undefined })}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="isRecorrente" className="flex items-center gap-2 cursor-pointer">
                    <Repeat className="h-4 w-4" />
                    Tornar esta conta recorrente
                  </Label>
                </div>
              )}
              
              {(formData.tipo_pagamento === 'recorrente' || formData.isRecorrente) && (
                <div className="pl-6 space-y-4 border-l-2">
                  <div className="space-y-2">
                    <Label htmlFor="frequencia">Frequência *</Label>
                    <Select value={formData.frequencia || ''} onValueChange={(value: any) => setFormData({ ...formData, frequencia: value, dia_semana: undefined, dia_mes: undefined })}>
                      <SelectTrigger>
                        <SelectValue placeholder={formData.tipo_pagamento === 'recorrente' ? "Selecione a frequência *" : "Selecione"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="diaria">Diária</SelectItem>
                        <SelectItem value="semanal">Semanal</SelectItem>
                        <SelectItem value="quinzenal">Quinzenal</SelectItem>
                        <SelectItem value="mensal">Mensal</SelectItem>
                        <SelectItem value="trimestral">Trimestral</SelectItem>
                        <SelectItem value="semestral">Semestral</SelectItem>
                        <SelectItem value="anual">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.frequencia === 'semanal' && (
                    <div className="space-y-2">
                      <Label htmlFor="dia_semana">Dia da Semana *</Label>
                      <Select value={formData.dia_semana?.toString()} onValueChange={(value) => setFormData({ ...formData, dia_semana: parseInt(value) })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o dia" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Domingo</SelectItem>
                          <SelectItem value="1">Segunda-feira</SelectItem>
                          <SelectItem value="2">Terça-feira</SelectItem>
                          <SelectItem value="3">Quarta-feira</SelectItem>
                          <SelectItem value="4">Quinta-feira</SelectItem>
                          <SelectItem value="5">Sexta-feira</SelectItem>
                          <SelectItem value="6">Sábado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {(formData.frequencia === 'quinzenal' || formData.frequencia === 'mensal' || formData.frequencia === 'trimestral' || formData.frequencia === 'semestral' || formData.frequencia === 'anual') && (
                    <div className="space-y-2">
                      <Label htmlFor="dia_mes">Dia do Mês *</Label>
                      <Input
                        id="dia_mes"
                        type="number"
                        min="1"
                        max="31"
                        value={formData.dia_mes || ''}
                        onChange={(e) => setFormData({ ...formData, dia_mes: parseInt(e.target.value) || undefined })}
                        placeholder="1-31"
                      />
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="data_fim_recorrencia">Data de Fim da Recorrência (opcional)</Label>
                    <Input
                      id="data_fim_recorrencia"
                      type="date"
                      value={formData.data_fim_recorrencia}
                      onChange={(e) => setFormData({ ...formData, data_fim_recorrencia: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Deixe em branco para recorrência sem data de fim
                    </p>
                  </div>
                </div>
              )}
            </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={salvarConta}>
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) {
          setDeletarOpcao('apenas');
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              {contaSelecionada?.tipo_pagamento === 'parcelado' && contaSelecionada.numero_parcela && contaSelecionada.quantidade_parcelas ? (
                <>
                  Esta é uma transação parcelada (Parcela {contaSelecionada.numero_parcela}/{contaSelecionada.quantidade_parcelas}). 
                  O que deseja excluir?
                </>
              ) : contaSelecionada?.tipo_pagamento === 'recorrente' && contaSelecionada.recorrente_id ? (
                <>
                  Esta é uma transação recorrente. O que deseja excluir?
                </>
              ) : (
                'Tem certeza que deseja excluir esta conta? Esta ação não pode ser desfeita.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {contaSelecionada?.tipo_pagamento === 'parcelado' && contaSelecionada.numero_parcela && contaSelecionada.quantidade_parcelas ? (
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="deletarOpcao"
                    value="apenas"
                    checked={deletarOpcao === 'apenas'}
                    onChange={() => setDeletarOpcao('apenas')}
                    className="h-4 w-4"
                  />
                  <span>Excluir apenas esta parcela ({contaSelecionada.numero_parcela}/{contaSelecionada.quantidade_parcelas})</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="deletarOpcao"
                    value="futuras"
                    checked={deletarOpcao === 'futuras'}
                    onChange={() => setDeletarOpcao('futuras')}
                    className="h-4 w-4"
                  />
                  <span>Excluir esta parcela e as parcelas futuras ({contaSelecionada.numero_parcela} até {contaSelecionada.quantidade_parcelas} - {contaSelecionada.quantidade_parcelas - contaSelecionada.numero_parcela + 1} parcela(s))</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="deletarOpcao"
                    value="todas"
                    checked={deletarOpcao === 'todas'}
                    onChange={() => setDeletarOpcao('todas')}
                    className="h-4 w-4"
                  />
                  <span>Excluir todas as {contaSelecionada.quantidade_parcelas} parcelas do parcelamento</span>
                </label>
              </div>
            </div>
          ) : contaSelecionada?.tipo_pagamento === 'recorrente' && contaSelecionada.recorrente_id ? (
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="deletarOpcao"
                    value="apenas"
                    checked={deletarOpcao === 'apenas'}
                    onChange={() => setDeletarOpcao('apenas')}
                    className="h-4 w-4"
                  />
                  <span>Excluir apenas esta transação</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="deletarOpcao"
                    value="futuras"
                    checked={deletarOpcao === 'futuras'}
                    onChange={() => setDeletarOpcao('futuras')}
                    className="h-4 w-4"
                  />
                  <span>Excluir esta transação e as transações futuras (a partir de {format(parseDateLocal(contaSelecionada.data_vencimento), "dd/MM/yyyy", { locale: ptBR })})</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="deletarOpcao"
                    value="todas"
                    checked={deletarOpcao === 'todas'}
                    onChange={() => setDeletarOpcao('todas')}
                    className="h-4 w-4"
                  />
                  <span>Excluir todas as transações desta recorrência</span>
                </label>
              </div>
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletarOpcao('apenas')}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={deletarConta} className="bg-destructive">
              {contaSelecionada?.tipo_pagamento === 'parcelado' && contaSelecionada.numero_parcela && contaSelecionada.quantidade_parcelas
                ? (deletarOpcao === 'todas' ? 'Excluir todas as parcelas' 
                   : deletarOpcao === 'futuras' ? 'Excluir parcelas futuras'
                   : 'Excluir esta parcela')
                : contaSelecionada?.tipo_pagamento === 'recorrente' && contaSelecionada.recorrente_id
                ? (deletarOpcao === 'todas' ? 'Excluir todas as transações' 
                   : deletarOpcao === 'futuras' ? 'Excluir transações futuras'
                   : 'Excluir esta transação')
                : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Escopo de Edição para Parcelas */}
      <AlertDialog open={editarEscopoDialogOpen} onOpenChange={(open) => {
        setEditarEscopoDialogOpen(open);
        if (!open) {
          setEditarEscopo('apenas');
          setDadosParaSalvar(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Como aplicar as alterações?</AlertDialogTitle>
            <AlertDialogDescription>
              {contaSelecionada?.tipo_pagamento === 'parcelado' && contaSelecionada.numero_parcela && contaSelecionada.quantidade_parcelas ? (
                <>
                  Esta é uma transação parcelada (Parcela {contaSelecionada.numero_parcela}/{contaSelecionada.quantidade_parcelas}). 
                  Como deseja aplicar as alterações feitas?
                </>
              ) : contaSelecionada?.tipo_pagamento === 'recorrente' && contaSelecionada.recorrente_id ? (
                <>
                  Esta é uma transação recorrente. Como deseja aplicar as alterações feitas?
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {contaSelecionada?.tipo_pagamento === 'parcelado' && contaSelecionada.numero_parcela && contaSelecionada.quantidade_parcelas && dadosParaSalvar ? (
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="editarEscopo"
                    value="apenas"
                    checked={editarEscopo === 'apenas'}
                    onChange={() => setEditarEscopo('apenas')}
                    className="h-4 w-4"
                  />
                  <span>Apenas esta parcela ({contaSelecionada.numero_parcela}/{contaSelecionada.quantidade_parcelas})</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="editarEscopo"
                    value="futuras"
                    checked={editarEscopo === 'futuras'}
                    onChange={() => setEditarEscopo('futuras')}
                    className="h-4 w-4"
                  />
                  <span>Esta parcela e as parcelas futuras ({contaSelecionada.numero_parcela} até {contaSelecionada.quantidade_parcelas})</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="editarEscopo"
                    value="todas"
                    checked={editarEscopo === 'todas'}
                    onChange={() => setEditarEscopo('todas')}
                    className="h-4 w-4"
                  />
                  <span>Todas as {contaSelecionada.quantidade_parcelas} parcelas do parcelamento</span>
                </label>
              </div>
              {dadosParaSalvar.quantidade_parcelas && dadosParaSalvar.quantidade_parcelas !== contaSelecionada.quantidade_parcelas && (
                <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm">
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">
                    {dadosParaSalvar.quantidade_parcelas < contaSelecionada.quantidade_parcelas 
                      ? `⚠️ Atenção: O número de parcelas será reduzido de ${contaSelecionada.quantidade_parcelas} para ${dadosParaSalvar.quantidade_parcelas}. As parcelas extras serão deletadas automaticamente.`
                      : `ℹ️ O número de parcelas será aumentado de ${contaSelecionada.quantidade_parcelas} para ${dadosParaSalvar.quantidade_parcelas}. Novas parcelas serão criadas automaticamente.`}
                  </p>
                </div>
              )}
            </div>
          ) : contaSelecionada?.tipo_pagamento === 'recorrente' && contaSelecionada.recorrente_id && dadosParaSalvar ? (
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="editarEscopo"
                    value="apenas"
                    checked={editarEscopo === 'apenas'}
                    onChange={() => setEditarEscopo('apenas')}
                    className="h-4 w-4"
                  />
                  <span>Apenas esta transação</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="editarEscopo"
                    value="futuras"
                    checked={editarEscopo === 'futuras'}
                    onChange={() => setEditarEscopo('futuras')}
                    className="h-4 w-4"
                  />
                  <span>Esta transação e as transações futuras (a partir de {format(parseDateLocal(contaSelecionada.data_vencimento), "dd/MM/yyyy", { locale: ptBR })})</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="editarEscopo"
                    value="todas"
                    checked={editarEscopo === 'todas'}
                    onChange={() => setEditarEscopo('todas')}
                    className="h-4 w-4"
                  />
                  <span>Todas as transações desta recorrência</span>
                </label>
              </div>
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setEditarEscopo('apenas');
              setDadosParaSalvar(null);
            }}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={aplicarAlteracoesParcela} className="bg-primary">
              Aplicar Alterações
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Alerta sobre Mudança de Quantidade de Parcelas */}
      <AlertDialog open={alertaParcelasDialogOpen} onOpenChange={(open) => {
        setAlertaParcelasDialogOpen(open);
        if (!open) {
          setAlertaParcelasInfo(null);
          setDialogOpen(false);
          setComprovanteFileForm(null);
          setBoletoFilesForm([]);
          setNotaFiscalFilesForm([]);
          carregarContas();
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {alertaParcelasInfo?.tipo === 'reducao' ? 'Parcelas Reduzidas' : 'Parcelas Adicionadas'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {alertaParcelasInfo?.tipo === 'reducao' ? (
                <>
                  O número de parcelas foi reduzido de <strong>{alertaParcelasInfo.quantidadeAtual}</strong> para <strong>{alertaParcelasInfo.quantidadeNova}</strong>.
                  <br />
                  <br />
                  <strong>{alertaParcelasInfo.quantidadeAtual - alertaParcelasInfo.quantidadeNova} parcela(s)</strong> foram deletadas automaticamente.
                </>
              ) : (
                <>
                  O número de parcelas foi aumentado de <strong>{alertaParcelasInfo?.quantidadeAtual}</strong> para <strong>{alertaParcelasInfo?.quantidadeNova}</strong>.
                  <br />
                  <br />
                  <strong>{alertaParcelasInfo && alertaParcelasInfo.quantidadeNova - alertaParcelasInfo.quantidadeAtual} nova(s) parcela(s)</strong> foram criadas automaticamente.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => {
              setAlertaParcelasDialogOpen(false);
              setAlertaParcelasInfo(null);
              setDialogOpen(false);
              setComprovanteFileForm(null);
              setBoletoFilesForm([]);
              setNotaFiscalFilesForm([]);
              carregarContas();
            }}>
              Entendi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Upload de Comprovante */}
      <Dialog open={comprovanteDialogOpen} onOpenChange={setComprovanteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Comprovante de Pagamento</DialogTitle>
            <DialogDescription>
              {contaSelecionada && `Conta: ${contaSelecionada.descricao}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {contaSelecionada?.comprovante_url && (
              <div className="p-4 border rounded-md bg-muted">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {contaSelecionada.comprovante_nome_arquivo || 'Comprovante atual'}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(contaSelecionada?.comprovante_url, '_blank')}
                  >
                    Ver
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="comprovante">
                Comprovante de Pagamento
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="comprovante"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('Arquivo muito grande. Tamanho máximo: 5MB');
                        return;
                      }
                      setComprovanteFile(file);
                    }
                  }}
                  className="hidden"
                />
                <Label
                  htmlFor="comprovante"
                  className="cursor-pointer flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent"
                >
                  <Upload className="h-4 w-4" />
                  {comprovanteFile ? comprovanteFile.name : 'Selecionar arquivo'}
                </Label>
                {comprovanteFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setComprovanteFile(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: PDF, JPG, PNG (máx. 5MB)
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setComprovanteDialogOpen(false);
                setComprovanteFile(null);
              }}>
                Cancelar
              </Button>
              <Button onClick={fazerUploadComprovante} disabled={!comprovanteFile || uploadingComprovante}>
                {uploadingComprovante ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Enviar Comprovante
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Documentos Anexados */}
      <Dialog open={documentosDialogOpen} onOpenChange={setDocumentosDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Documentos Anexados</DialogTitle>
            <DialogDescription>
              {contaParaDocumentos && `Documentos da transação: ${contaParaDocumentos.descricao}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {contaParaDocumentos && (
              <>
                {/* Comprovante de Pagamento */}
                {contaParaDocumentos.comprovante_url && (
                  <div className="p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="p-2 bg-primary/10 rounded-md">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">Comprovante de Pagamento</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {contaParaDocumentos.comprovante_nome_arquivo || 'Comprovante'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => visualizarArquivo(contaParaDocumentos.comprovante_url!)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Visualizar</p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => baixarArquivo(
                                contaParaDocumentos.comprovante_url!,
                                contaParaDocumentos.comprovante_nome_arquivo || 'comprovante.pdf'
                              )}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Download</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                )}

                {/* Boletos */}
                {getBoletosDaConta(contaParaDocumentos).map((anexo, index) => (
                  <div
                    key={anexo.id}
                    className="p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="p-2 bg-primary/10 rounded-md">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">
                            {getBoletosDaConta(contaParaDocumentos).length > 1
                              ? `Boleto ${index + 1}`
                              : 'Boleto'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {anexo.nome_arquivo || 'Boleto'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => visualizarArquivo(anexo.url)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Visualizar</p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                baixarArquivo(anexo.url, anexo.nome_arquivo || 'boleto.pdf')
                              }
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Download</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Notas Fiscais */}
                {getNotasFiscaisDaConta(contaParaDocumentos).map((anexo, index) => (
                  <div
                    key={anexo.id}
                    className="p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="p-2 bg-primary/10 rounded-md">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">
                            {getNotasFiscaisDaConta(contaParaDocumentos).length > 1
                              ? `Nota Fiscal ${index + 1}`
                              : 'Nota Fiscal'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {anexo.nome_arquivo || 'Nota Fiscal'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => visualizarArquivo(anexo.url)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Visualizar</p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                baixarArquivo(
                                  anexo.url,
                                  anexo.nome_arquivo || 'nota_fiscal.pdf'
                                )
                              }
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Download</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Mensagem quando não há documentos */}
                {!contaParaDocumentos.comprovante_url &&
                  getBoletosDaConta(contaParaDocumentos).length === 0 &&
                  getNotasFiscaisDaConta(contaParaDocumentos).length === 0 && (
                    <div className="text-center p-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhum documento anexado a esta transação</p>
                    </div>
                  )}
              </>
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setDocumentosDialogOpen(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

